import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  Control,
  Controller,
  useForm,
  UseFormRegister,
  SubmitHandler,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ContactUsFormFields, ContactUsProps } from './types';
import { SUPPORT_CATEGORIES, DCIM_API_URLS } from './constants';
import {
  contactUsValidationSchema,
  basicContactUsValidationSchema,
} from './validationSchema';
import { enqueueSnackbar } from 'notistack';
import FileAttachment from './FileAttachment/FileAttachment';
import {
  StyledPaper,
  StyledContent,
  FormContainer,
  TitleContainer,
  FormRow,
  BackRow,
  ButtonContainer,
  RequiredFieldNote,
  SmoothReveal,
  useSmoothRevealClasses,
  SectionDivider,
} from './ContactUsStyles';
import DcimDetails from './DcimDetails/DcimDetails';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { createPhoneChangeHandler } from '../../shared/utils';
import { PHONE_MAX_LENGTH } from '../../shared/constants';
import { useSupportRequest } from './useSupportRequest';
import { useApiEssentials } from '../../shared/hooks/useApiEssentials';
import { useUserDateFormatter } from '../../shared/hooks/useUserDateFormatter';
import { ApiError } from '../../shared/api/apiService';
import { dxPortalBasicFeaturesPermission } from '@internal/shared/src/permission/opa_permissions';
import { usePermission } from '@backstage/plugin-permission-react';

function getContactUsDefaultValues(isAdvanced: boolean): ContactUsFormFields {
  if (isAdvanced) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      primaryEmail: '',
      primaryPhone: '',
      category: SUPPORT_CATEGORIES[0],
      description: '',
      message: '',
      dcimDataCenterLocation: '',
      dcimUrl: '',
      dcimProfileId: '',
      dcimDdidSample: '',
      isNotTimeDependent: false,
      startUtc: null,
      endUtc: null,
    };
  }
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    description: '',
    message: '',
  } as ContactUsFormFields;
}

const ContactUsPage = ({}: ContactUsProps) => {
  const navigate = useNavigate();
  const revealClasses = useSmoothRevealClasses();
  const [attachments, setAttachments] = useState<FileList | null>(null);
  const { isSignedIn } = useApiEssentials();

  const { allowed: isAdvancedForm } = usePermission({
    permission: dxPortalBasicFeaturesPermission,
    resourceRef: undefined,
  });
  const { mutate: sendContactUsForm, isPending } =
    useSupportRequest(isAdvancedForm);
  const { pickerFormat, is24HourFormat } = useUserDateFormatter();

  const handleBackAction = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const validationSchema = isAdvancedForm
    ? contactUsValidationSchema
    : basicContactUsValidationSchema;

  const defaultValues = useMemo(
    () => getContactUsDefaultValues(isAdvancedForm),
    [isAdvancedForm],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    control,
    setError,
    clearErrors,
  } = useForm<ContactUsFormFields>({
    resolver: yupResolver(validationSchema) as any,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<ContactUsFormFields> = async data => {
    clearErrors();

    if (isAdvancedForm && !isSignedIn) {
      enqueueSnackbar('You are not authenticated', { variant: 'error' });
      return;
    }

    // Filter data based on permissions - only send fields user has access to
    const submissionData = isAdvancedForm
      ? data
      : {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          description: data.description,
          message: data.message,
        };

    const submissionPayload = isAdvancedForm
      ? { ...submissionData, attachments }
      : submissionData;

    sendContactUsForm(submissionPayload as any, {
      onSuccess: () => {
        enqueueSnackbar(
          'Your support request has been successfully submitted',
          {
            variant: 'success',
          },
        );
        reset(defaultValues);
        setAttachments(null);
      },
      onError: (err: unknown) => {
        const apiErr = err as ApiError;
        const errorPayload = apiErr?.data || apiErr;
        const issue = errorPayload?.errors?.[0];

        if (issue?.field) {
          setError(issue.field as keyof ContactUsFormFields, {
            type: 'server',
            message: issue?.message || 'Invalid value',
          });
        }

        const msg =
          issue[0]?.message ||
          errorPayload?.detail ||
          apiErr?.message ||
          'Failed to submit support request.';
        enqueueSnackbar(msg, { variant: 'error' });
      },
    });
  };

  const dcimUrls = useMemo(() => {
    const origin = window.location.origin;
    const set = new Set<string>([...DCIM_API_URLS]);
    if (origin) set.add(origin);
    return Array.from(set);
  }, []);

  const selectedCategory = watch('category');
  const isNotTimeDependent = watch('isNotTimeDependent');
  const isDcimSelected = selectedCategory === 'DCIM';

  return (
    <StyledContent>
      <StyledPaper>
        <BackRow>
          <Button
            startIcon={<ArrowBackIcon />}
            color="primary"
            style={{ fontWeight: 700 }}
            onClick={handleBackAction}
          >
            Back
          </Button>
        </BackRow>
        <Typography
          variant="h3"
          id="contact-us-page-title"
          style={{ marginBottom: 24 }}
        >
          Request Support
        </Typography>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <TitleContainer>
            <Typography
              variant="subtitle1"
              style={{ fontSize: 20, fontWeight: 700 }}
            >
              Support Form
            </Typography>
            <RequiredFieldNote variant="caption">
              *denotes required field
            </RequiredFieldNote>
          </TitleContainer>
          <FormRow>
            <TextField
              label="First Name"
              required
              fullWidth
              {...register('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message || ' '}
            />
            <TextField
              label="Last Name"
              required
              fullWidth
              {...register('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName?.message || ' '}
            />
          </FormRow>
          <FormRow>
            <TextField
              label="Email Address"
              required
              fullWidth
              type="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message || ' '}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Phone Number"
                  required
                  fullWidth
                  type="tel"
                  inputProps={{
                    maxLength: PHONE_MAX_LENGTH,
                  }}
                  onChange={createPhoneChangeHandler(field.onChange)}
                  error={!!error}
                  helperText={error?.message || ' '}
                />
              )}
            />
          </FormRow>
          {isAdvancedForm && (
            <>
              <FormRow>
                <TextField
                  label="Primary Contact Email Address"
                  required
                  fullWidth
                  type="email"
                  {...register('primaryEmail')}
                  error={!!errors.primaryEmail}
                  helperText={errors.primaryEmail?.message || ' '}
                />
                <Controller
                  name="primaryPhone"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Primary Contact Phone Number"
                      required
                      fullWidth
                      type="tel"
                      inputProps={{
                        maxLength: PHONE_MAX_LENGTH,
                      }}
                      onChange={createPhoneChangeHandler(field.onChange)}
                      error={!!error}
                      helperText={error?.message || ' '}
                    />
                  )}
                />
              </FormRow>
              <SectionDivider />
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl required fullWidth error={!!errors.category}>
                    <InputLabel>Support Category</InputLabel>
                    <Select
                      label="Support Category"
                      value={field.value ?? SUPPORT_CATEGORIES[0]}
                      style={{ marginBottom: 4 }}
                      onChange={e => {
                        field.onChange(e.target.value);
                      }}
                    >
                      {SUPPORT_CATEGORIES.map(categoryOption => (
                        <MenuItem key={categoryOption} value={categoryOption}>
                          {categoryOption}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.category && (
                      <FormHelperText>{errors.category.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <SmoothReveal
                className={
                  isDcimSelected ? revealClasses.open : revealClasses.closed
                }
              >
                <DcimDetails
                  control={control as Control<ContactUsFormFields>}
                  register={register as UseFormRegister<ContactUsFormFields>}
                  errors={errors}
                  dcimUrls={dcimUrls}
                />
              </SmoothReveal>
              <SectionDivider />
              <Box zIndex={100}>
                <Controller
                  name="isNotTimeDependent"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={e => field.onChange(e.target.checked)}
                          inputRef={field.ref}
                          color="primary"
                        />
                      }
                      label="Issue is not time dependent"
                    />
                  )}
                />
              </Box>
              <Typography
                variant="body2"
                style={{ marginTop: 4, marginBottom: 8 }}
              >
                Please provide the approx. start and end time (UTC) that the
                issue occurred
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormRow>
                  <Box style={{ flex: 1 }}>
                    <Controller
                      name="startUtc"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          label="Start"
                          value={field.value || null}
                          onChange={(date: Date | null) => {
                            field.onChange(date);
                          }}
                          disabled={!!isNotTimeDependent}
                          format={pickerFormat}
                          ampm={!is24HourFormat}
                          slotProps={{
                            textField: {
                              required: !isNotTimeDependent,
                              fullWidth: true,
                              error: !!errors.startUtc && !isNotTimeDependent,
                              helperText:
                                (!isNotTimeDependent &&
                                  errors.startUtc?.message) ||
                                ' ',
                            },
                          }}
                          disableFuture
                        />
                      )}
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Controller
                      name="endUtc"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          label="End"
                          value={field.value || null}
                          onChange={(date: Date | null) => {
                            field.onChange(date);
                          }}
                          disabled={!!isNotTimeDependent}
                          format={pickerFormat}
                          ampm={!is24HourFormat}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.endUtc,
                              helperText: errors.endUtc?.message || ' ',
                            },
                          }}
                          disableFuture
                        />
                      )}
                    />
                  </Box>
                </FormRow>
              </LocalizationProvider>{' '}
              <SectionDivider />
            </>
          )}

          <TextField
            label="Brief Description"
            required
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description?.message || ' '}
          />
          <TextField
            label="Message"
            required
            {...register('message')}
            error={!!errors.message}
            helperText={errors.message?.message || ' '}
            multiline
            minRows={2}
          />
          {isAdvancedForm && (
            <FileAttachment
              attachments={attachments}
              onAttachmentsChange={setAttachments}
            />
          )}
          <ButtonContainer>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="primary"
              variant="contained"
              disabled={isSubmitting || isPending}
              startIcon={
                isPending ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                    style={{ marginRight: 8 }}
                  />
                ) : null
              }
            >
              SUBMIT
            </Button>
          </ButtonContainer>
        </FormContainer>
      </StyledPaper>
    </StyledContent>
  );
};

export default ContactUsPage;
